
import {of as observableOf, forkJoin as observableForkJoin, merge as observableMerge,  Observable ,  BehaviorSubject ,  Subject ,  Subscription , concat} from 'rxjs';

import {mergeMap, map, debounceTime, distinctUntilChanged , reduce} from 'rxjs/operators';
import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';



import { TranslateService } from '@ngx-translate/core';
import { INode } from './INode';
import { IAttribute } from '../attribute/IAttribute';
import { LoggerService } from '../logger-service/logger.service';
import { OxeModelService } from '../oxe-model-service/oxe-model.service';
import { ConfigSearchService } from '../config-breadcrumb/config-search/config-search.service';


@Component({
    selector: 'wbm-config-tree-node',
    templateUrl: './config-tree-node.component.html',
    styleUrls: ['../../assets/theme/css/ConfigTree.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ConfigTreeNodeComponent implements OnInit, OnDestroy {
    @Input() public node: INode;
    selectedNode: INode;

    private isValid: boolean = true;
    private isLocal: boolean = false;
    private isEdited: boolean = false;
    private activeChangeSubscriptions: Array<Subscription> = [];
    private _isActive: BehaviorSubject<boolean>;

    displayName: Observable<string>;
    private triggerDisplayNameUpdate: Subject<boolean>;

    private iconName: string;
    iconPath: string;
    private triggerIconPathUpdate: Subject<boolean>;

    private isExpanded: boolean = false;
    private expandChangeSubscriptions: Array<Subscription> = [];

    private isExpandCollapseIconHidden: boolean = false;
    private triggerExpandCollapseIconVisibilityUpdate: Subject<boolean>;

    private subscriptions: Array<Subscription> = [];

    private currentPage: number = 1;
    private _nbPages: number = 1;
    private indexFirstItem: number = 0;
    private indexLastItem: number = 1;

    constructor(
        private _logger: LoggerService,
        private _oxeModel: OxeModelService,
        private _translate: TranslateService,
        private cd: ChangeDetectorRef,
        private _searchService: ConfigSearchService
    ) {
        this._isActive = new BehaviorSubject(false);
    }

    get isActive(): boolean {
        return this._isActive.getValue();
    }

    set isActive(isUsed: boolean) {
        this._isActive.next(isUsed);
    }

    private isActiveChanges(): Observable<boolean> {
        return this._isActive.asObservable().pipe(distinctUntilChanged());
    }

    get isLeaf(): boolean {
        return this.node.isObjectNode || (this.node.isOneInstance && !this.node.hasSubClass());
    }

    ngOnInit() {
        this._logger.debug('ConfigTreeNodeComponent', this.node.getPath() + ' is a ' + (this.node.isClassNode ? 'Class' : 'Object'));

        if (this.node.isObjectNode || (this.node.isClassNode && !this.node.isOneInstance)) {
            let filteringAttributes = this.node.getFilteringAttributes();
            let initialAttributes = this.node.getInitialAttributesToRetrieve();
            this._logger.debug('ConfigTreeNodeComponent', '   - filterable attributes= ' + filteringAttributes.length + ' [' + filteringAttributes.join(' , ') + ']');
            this._logger.debug('ConfigTreeNodeComponent', '   - initial attributes= ' + initialAttributes.length + ' [' + initialAttributes.join(' , ') + ']');
        }

        // INITIAL STATUS AND EVENT LISTENERS
        this.addLocalChangeListeners(); // isValid, isEdited , display name, icon path and expand/collapse icon are initialized here

        this.subscriptions.push(this._oxeModel.selectedNodeObserver
            .subscribe(selectedNode => {
                this.selectedNode = selectedNode;
                if (this.isActive !== (this.selectedNode === this.node)) {
                    this.isActive = (this.selectedNode === this.node);
                    if (this.isActive) {
                        this.addActiveChangeListeners();
                    } else {
                        this.removeActiveChangeListeners();
                    }
                    this.updateExpandCollapseIconVisibility();
                    this.updateNodeIconPath();
                }
                // check if an unselected node is in the path of the selected node (if any) or not?
                if (!this.isActive && !this.isLocal && this.selectedNode) {
                    if (this.selectedNode.getPath().startsWith(this.node.getPath().concat('/'))) {
                        this.node.isExpanded = true; // trigger an expand to expand the whole path
                    }
                }
            }));

        this.subscriptions.push(this.node.isExpandedChanges().subscribe(isExpanded => {
            if (isExpanded) {
                this.addExpandChangeListeners();
            } else {
                this.removeExpandChangeListeners();
            }
            this.isExpanded = isExpanded;
            this.cd.markForCheck();
        }));

        this.subscriptions.push(this.node.isFilterAppliedChanges().subscribe(() => {
            this.currentPage = 1;
            this.cd.markForCheck();
        }));

        // DisplayName Update Trigger based on changes in observables
        if (this.node.isObjectNode) {
            this.triggerDisplayNameUpdate = new Subject();
            this.node.getInitialAttributesToRetrieve().forEach(attr => {
                this.subscriptions.push(observableMerge(attr.isUsedChanges(), attr.valueChanges(), attr.localValueChanges())
                    .subscribe(() => {
                        this.triggerDisplayNameUpdate.next();
                    }));
            });

            this.subscriptions.push(this.triggerDisplayNameUpdate.pipe(debounceTime(50))
                .subscribe(() => {
                    this.updateNodeDisplayName();
                    this.cd.markForCheck();
                }));
        }

        this.subscriptions.push(this.isActiveChanges().subscribe(() => this.cd.markForCheck()));
    }

    ngOnDestroy() {
        this.removeActiveChangeListeners();
        this.removeExpandChangeListeners();

        //release global subscriptions
        this.removeGlobalSubscriptions();
    }

    private removeGlobalSubscriptions() {
        while (this.subscriptions.length > 0) {
            this.subscriptions.pop().unsubscribe();
        }
    }

    public select(): void {
        this._logger.debug('ConfigTreeNodeComponent', 'select ' + this.node.name);
        this._searchService.showSearch = false;
        if (this.isLocal) {
            this._oxeModel.selectNode(this.node);
        } else {
            // Retrieve data only for object nodes or "Oneinstance" class nodes
            if (this.node.isClassNode && !this.node.isOneInstance) {
                this._oxeModel.selectNode(this.node);
            } else {
                this._oxeModel.retrieveData(this.node)
                    .subscribe(() => {
                        this._oxeModel.selectNode(this.node);
                        this.cd.markForCheck();
                    });
            }
        }
        this._oxeModel.helpNode(this.node);
    }

    public toggle(select = false): void {
        this._searchService.isTreeToggle = true;
        if (!this.isLocal && !this.isExpandCollapseIconHidden) {
            this._logger.debug('ConfigTreeNodeComponent', 'toggle ' + this.node.name);
            if (!this.node.isExpanded) {
                this._oxeModel.retrieveSubNodes(this.node)
                    .subscribe(() => {
                        this.node.isExpanded = true;
                        this.cd.markForCheck();
                        if (select) {
                            this._oxeModel.selectNode(this.node);
                        }
                    });
            } else {
                if (this.node.isClassNode && !this.node.isOneInstance) {
                    this._oxeModel.removeUnchangedSubNodes(this.node, false);
                }
                this.node.isExpanded = false;
            }
        }
    }

    private addLocalChangeListeners() {
        let node = this.node;
        if (this.node.isClassNode && this.node.isOneInstance && this.node.subNodes && this.node.subNodes.length > 0) {
            node = this.node.subNodes[0];
        }
        // Initial status
        this.isValid = this.node.isValid;
        this.isEdited = this.node.isAttributesModified;
        this.isLocal = this.node.isLocal;

        // Listener to isLocal value changes
        this.subscriptions.push(node.isLocalChanges()
            .subscribe(isLocal => {
                this.isLocal = isLocal;
                this.updateExpandCollapseIconVisibility();
                this.updateNodeIconPath();
                this.updateNodeDisplayName();
                this.cd.markForCheck();
            }));
    }

    private addActiveChangeListeners() {
        if (this.activeChangeSubscriptions.length === 0) {
            let node = this.node;
            if (this.node.isClassNode && this.node.isOneInstance && this.node.subNodes && this.node.subNodes.length > 0) {
                node = this.node.subNodes[0];
            }
            this.activeChangeSubscriptions.push(node.isValidChanges()
                .subscribe(isValid => {
                    this.isValid = isValid;
                    this.cd.markForCheck();
                }));
            this.activeChangeSubscriptions.push(node.isAttributesModifiedChanges()
                .subscribe(isAttributesModified => {
                    this.isEdited = isAttributesModified;
                    this.cd.markForCheck();
                }));
            // IconPath Update Trigger based on changes in observables
            this.triggerIconPathUpdate = new Subject();
            this.activeChangeSubscriptions.push(observableMerge(node.isValidChanges(), node.isAttributesModifiedChanges())
                .subscribe(() => {
                    this.triggerIconPathUpdate.next();
                    this.cd.markForCheck();
                }));
            this.activeChangeSubscriptions.push(this.triggerIconPathUpdate.pipe(debounceTime(50))
                .subscribe(() => {
                    this.updateNodeIconPath();
                    this.cd.markForCheck();
                }));
            this.activeChangeSubscriptions.push(node.sortOrderChanges()
                .subscribe(() => {
                    this.cd.markForCheck();
                }));
        }
    }

    private removeActiveChangeListeners() {
        while (this.activeChangeSubscriptions.length > 0) {
            this.activeChangeSubscriptions.pop().unsubscribe();
        }
    }

    private addExpandChangeListeners() {
        if (this.expandChangeSubscriptions.length === 0) {
            let node = this.node;
            if (this.node.isClassNode && this.node.isOneInstance && this.node.subNodes && this.node.subNodes.length > 0) {
                node = this.node.subNodes[0];
            }
            if (node.subNodes) {
                // the subnodes has been retrieved, so we can determine if a expand/collpase button is legitim
                if (node.subNodes.length > 0) {
                    // ExpandIcon Visibility Update Trigger based on changes in observables
                    this.triggerExpandCollapseIconVisibilityUpdate = new Subject();
                    let changesObservables = [];
                    node.subNodes.forEach(subNode => {
                        changesObservables.push(subNode.isUsedChanges());
                    });
                    this.expandChangeSubscriptions.push(observableMerge.apply(this, changesObservables)
                        .subscribe(() => {
                            this.triggerExpandCollapseIconVisibilityUpdate.next();
                            this.cd.markForCheck();
                        }));
                    this.expandChangeSubscriptions.push(this.triggerExpandCollapseIconVisibilityUpdate.pipe(debounceTime(50))
                        .subscribe(() => {
                            this.updateExpandCollapseIconVisibility();
                            this.cd.markForCheck();
                        }));
                }
                this.updateExpandCollapseIconVisibility();
            }
        }
    }

    private removeExpandChangeListeners() {
        while (this.expandChangeSubscriptions.length > 0) {
            this.expandChangeSubscriptions.pop().unsubscribe();
        }
    }

    private updateNodeIconPath(): void {
        if (!this.iconName) {
            this.iconName = this.getNodeIconName();
        }
        this.iconPath = this.getNodeIconPath(this.iconName);
    }

    private getNodeIconName(): string {
        let icon = 'icon__folder';

        switch (this.node.name) {
            case 'Dect_System':
                icon = 'icon__dect-system';
                break;
            case 'System_Parameters':
                icon = 'icon__system';
                break;
            case 'Subscriber':
                icon = 'icon__users';
                break;
            case 'Group':
                icon = 'icon__groups';
                break;
            case 'Phone_Book':
                icon = 'icon__phone-book';
                break;
            case 'Security_Access':
                icon = 'icon__security-access';
                break;
            case 'SIP':
                icon = 'icon__sip';
                break;
            case 'NOE':
                icon = 'icon__al89';
                break;
            case 'RAINBOW':
                icon = 'icon__logo_rainbow';
                break;
            default:
                if (this.node.isObjectNode || (this.node.isOneInstance && !this.node.hasSubClass())) {
                    icon = 'icon__file';
                } else if (this.node.isOneInstance && this.node.attributes.length > 1) {
                    icon = 'icon__folder__attr';
                } else {
                    icon = 'icon__folder';
                }
        }
        return icon;
    }

    private getNodeIconPath(iconName: string): string {
        this._logger.debug('ConfigTreeNodeComponent', this.node.getPath() + ' : getNodeIconPath()');
        let baseUrl = '/assets/theme/img/sprite.svg#' + iconName;

        if (baseUrl.endsWith('icon__file')) {
            if (this.isLocal && this.isValid) {
                baseUrl += '-local';
            }
        }

        if (!this.isValid) {
            baseUrl += '-notvalid';
        } else if (this.isEdited && !this.isLocal) {
            baseUrl += '-edited';
        }

        if (this.isActive) {
            baseUrl += '--active';
        }

        return baseUrl;
    }

    private updateNodeDisplayName(): void {
        this.displayName = this.getNodeLabel();
    }

    private getNodeLabel(): Observable<string> {
        this._logger.debug('ConfigTreeNodeComponent', this.node.getPath() + ' : getNodeLabel()');
        if (this.node.isObjectNode) {
            let obsAttribArray = this.getLabelDisplayedAttributes().map(attr => this._oxeModel.resolveAttributeString(attr));
            let obsAttrib = observableForkJoin(obsAttribArray).pipe(
                map(labels => labels.filter(val => val !== '' && val !== undefined).join(' \u2022 ')));

            if (this.isLocal) {
                return obsAttrib.pipe(mergeMap(attribString => {
                    let obsUnsavedLabel: Observable<string> = this._translate.get('unsaved-node');
                    let colon: string = !!attribString ? ': ' : '';
                    let obsUnsavedIndex: Observable<string> = observableOf('#' + this.node.localIdx + colon);

return concat(obsUnsavedLabel, obsUnsavedIndex, observableOf(attribString)).pipe(
                          reduce((a, b) => a + b)
                        );                }));
            } else {
                return obsAttrib;
            }
        } else {
            return this._translate.get(this.node.name);
        }
    }

    private getLabelDisplayedAttributes(limit = 5): Array<IAttribute> {
        // Remove the unused attributes (depending on the used_when condition evaluation)
        let displayNameAttributes = this.node.getInitialAttributesToRetrieve().filter((attr: IAttribute) => attr.isUsed);

        return displayNameAttributes.slice(0, limit); // limit to 5 attributes
    }

    private updateExpandCollapseIconVisibility(): void {
        this.isExpandCollapseIconHidden = this.shouldHideExpandCollapseIcon();
    }

    private shouldHideExpandCollapseIcon(): boolean {
        if (this.isLocal) {
            return true;
        }

        if ((this.node.isObjectNode || this.node.isOneInstance)) {
            if (!this.node.hasSubClass()) {
                return true;
            } else {
                if (!!this.node.subNodes && (this.node.subNodes.length === 0 || this.node.subNodes.every(subNode => !subNode.isUsed))) {
                    // the list of subnodes have been retrieved, but is empty or all are not used (ie. used_when condition)
                    return true;
                }
            }
        }

        return false;
    }

    previousPage(): void {
        if (this.currentPage > 1) {
            this.currentPage--;

            this.computeIndexesFromCurrentPage();
            this.node.isExpanded = true;
            this.cd.markForCheck();
        }
    }

    nextPage(): void {
        if (this.currentPage < this.nbPages) {
            this.currentPage++;
            this.computeIndexesFromCurrentPage();
            this.node.isExpanded = true;
            this.cd.markForCheck();
        }
    }

    onGotoPage(pageDesti: string): void {
        this.currentPage = parseInt(pageDesti, 10);
        this.computeIndexesFromCurrentPage();
        this.node.isExpanded = true;
        this.cd.markForCheck();
    }

    get nbPages(): number {
        return this.computeNbPages();
    }

    get pageList(): Array<number> {
        const res = [];
        for (let i = 0; i < this._nbPages; i++) {
            res.push(i + 1);
        }
        return res;
    }

    get displayedCounters(): string {
        this.computeIndexesFromCurrentPage();
        let countersToDisplay = '';
        if (this._nbPages > 1) {
            countersToDisplay = (this.indexFirstItem + 1) + '-' + this.indexLastItem + '/' + this.node.subNodes.length;
        } else if (this.node.subNodes.length !== this.node.subNodesInitialLength) {
            countersToDisplay = this.node.subNodes.length + '/' + this.node.subNodesInitialLength;
        } else {
            countersToDisplay = this.node.subNodes.length + '';
        }

        return countersToDisplay;
    }

    getDisplayedSubNodes(): INode[] {
        this.computeIndexesFromCurrentPage();
        return this.node.getDisplayedSubNodes(this.indexFirstItem, this.indexLastItem);
    }

    private computeNbPages(): number {
        if (this.node.subNodes && this.node.maxDisplayedSubNodes !== 0) {
            const remainder = this.node.subNodes.length % this.node.maxDisplayedSubNodes;
            const q = this.node.subNodes.length / this.node.maxDisplayedSubNodes;
            this._nbPages = remainder === 0 ? q : Math.trunc(q) + 1;
        } else {
            this._nbPages = 1;
        }
        return this._nbPages;
    }

    private computeIndexesFromCurrentPage(): void {
        if (this.node.subNodes && this.node.maxDisplayedSubNodes !== 0) {
            this.computeNbPages();
            this.indexFirstItem = (this.currentPage - 1) * this.node.maxDisplayedSubNodes;
            this.indexLastItem = this.currentPage === this._nbPages ?
                this.node.subNodes.length :
                this.currentPage * this.node.maxDisplayedSubNodes;
        } else {
            this.indexFirstItem = 0;
            this.indexLastItem = this.node.subNodes ? this.node.subNodes.length : 1;
        }
    }
}
